import React from 'react';
import { ChevronDown, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { vanSpecs, fleet } from '../data/mock';

const HeroSection = () => {
  const scrollToEnquiry = () => {
    const element = document.querySelector('#enquiry');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToVehicle = () => {
    const element = document.querySelector('#vehicle');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-50 via-sky-50 to-slate-100 pt-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-sky-200 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-100 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-sky-500 rounded-full animate-pulse" />
              Trade Professionals Only
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              Window Cleaning Van Hire
              <span className="block text-sky-600">Sussex & South East</span>
            </h1>
            
            <p className="text-lg text-slate-500 mb-2">
              East Sussex • West Sussex • Brighton • Worthing | <span className="font-semibold text-sky-600">£500/week</span>
            </p>
            
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0">
              Looking for <span className="font-medium text-slate-700">WFP van hire near you</span>? Our window cleaning van hire service provides fully-fitted vehicles to trade professionals across Sussex, with the 
              <span className="font-medium text-slate-700"> vehicle insured by the user.</span>
            </p>

            {/* Quick Features */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-6">
              {[vanSpecs.tank, vanSpecs.heating, vanSpecs.reels].map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-sky-600" />
                  <span className="font-medium">{feature}</span>
                </div>
              ))}
            </div>

            {/* Important Note */}
            <div className="flex items-center gap-2 justify-center lg:justify-start text-amber-700 bg-amber-50 px-4 py-2 rounded-lg mb-10 w-fit mx-auto lg:mx-0">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium text-sm">No poles supplied – bring your own</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button 
                onClick={scrollToEnquiry}
                size="lg"
                className="bg-sky-600 hover:bg-sky-700 text-white text-lg px-8 py-6 shadow-lg shadow-sky-600/25 hover:shadow-xl hover:shadow-sky-600/30 transition-all duration-300"
                data-testid="hero-enquiry-btn"
              >
                Enquire About Availability
              </Button>
              <Button 
                onClick={scrollToVehicle}
                variant="outline"
                size="lg"
                className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 text-lg px-8 py-6 transition-all duration-300"
                data-testid="hero-vehicle-btn"
              >
                View Our Fleet
              </Button>
            </div>
          </div>

          {/* Side-by-Side Van Images */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              {fleet.map((van, index) => (
                <div key={van.id} className="relative group">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl bg-slate-100 transition-transform duration-300 group-hover:scale-[1.02]">
                    <img 
                      src={van.heroImage} 
                      alt={van.name}
                      className="w-full h-48 sm:h-56 lg:h-64 object-cover"
                    />
                    {/* Van Name Badge */}
                    <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-sm rounded-xl px-3 py-2 shadow-lg">
                      <p className="font-bold text-slate-900 text-sm">{van.name}</p>
                      <p className="text-xs text-slate-500">{van.engine} • {van.year}</p>
                    </div>
                  </div>
                  {index === 0 && (
                    <div className="absolute -top-2 -right-2 bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                      Available
                    </div>
                  )}
                  {index === 1 && (
                    <div className="absolute -top-2 -right-2 bg-sky-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                      New
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Floating Stats */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-xl p-4 flex gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-sky-600">2</div>
                <div className="text-xs text-slate-500">Vans</div>
              </div>
              <div className="w-px bg-slate-200" />
              <div className="text-center">
                <div className="text-2xl font-bold text-sky-600">650L</div>
                <div className="text-xs text-slate-500">Tank</div>
              </div>
              <div className="w-px bg-slate-200" />
              <div className="text-center">
                <div className="text-2xl font-bold text-sky-600">Hot</div>
                <div className="text-xs text-slate-500">Water</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-slate-400" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
